import { CarouselScroller, CarouselTab, CarouselTabs } from 'react-aria-carousel';
import styled from 'styled-components';

export const Wrapper = styled.section`
  padding: 24px 0 16px;
  background: var(--white);

  @media (width < 48rem) {
    padding: 12px 0;
  }
`;

export const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 5;
  min-height: 360px;
  max-height: 520px;
  border-radius: 14px;
  overflow: hidden;
  isolation: isolate;

  @media (width < 48rem) {
    aspect-ratio: auto;
    min-height: 0;
    max-height: none;
    height: 540px;
    border-radius: 12px;
  }
`;

export const Background = styled.div<{ $theme: string }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  ${props => {
    switch (props.$theme) {
      case 'olive':
        return `background:
          radial-gradient(ellipse 50% 35% at 25% 30%, rgb(255 255 255 / 12%), transparent 70%),
          radial-gradient(ellipse 40% 28% at 80% 70%, rgb(0 0 0 / 12%), transparent 70%),
          linear-gradient(150deg, #6b7d18 0%, #526103 50%, #3d4a02 100%);`;
      case 'amber':
        return `background:
          radial-gradient(ellipse 60% 40% at 30% 25%, rgb(255 220 160 / 22%), transparent 70%),
          radial-gradient(ellipse 50% 30% at 75% 75%, rgb(120 50 20 / 18%), transparent 60%),
          linear-gradient(160deg, #c8722a 0%, #a04e1a 50%, #6e3210 100%);`;
      case 'plum':
        return `background:
          radial-gradient(ellipse 55% 35% at 20% 30%, rgb(255 255 255 / 8%), transparent 60%),
          radial-gradient(ellipse 50% 30% at 80% 70%, rgb(0 0 0 / 25%), transparent 60%),
          linear-gradient(155deg, #5b3a78 0%, #3d2655 50%, #221638 100%);`;
      default: // poolside
        return `background:
          radial-gradient(ellipse 60% 30% at 30% 25%, rgb(255 255 255 / 14%), transparent 60%),
          radial-gradient(ellipse 50% 22% at 75% 70%, rgb(255 255 255 / 10%), transparent 60%),
          radial-gradient(ellipse 40% 18% at 20% 80%, rgb(255 255 255 / 8%), transparent 60%),
          linear-gradient(165deg, #2d8aa6 0%, #3aa3bd 35%, #2a7d9e 70%, #1f5b76 100%);`;
    }
  }}
`;

export const Vignette = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(90deg, rgb(0 0 0 / 28%) 0%, rgb(0 0 0 / 8%) 38%, rgb(0 0 0 / 0%) 60%),
    linear-gradient(180deg, rgb(0 0 0 / 0%) 60%, rgb(0 0 0 / 16%) 100%);
  pointer-events: none;
`;

export const Scroller = styled(CarouselScroller)`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 100%;
  overflow: hidden;
  position: absolute;
  inset: 0;
  z-index: 2;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
`;

export const Slide = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 80px;

  @media (width < 48rem) {
    padding: 28px 22px 56px;
    align-items: start;
  }
`;

export const Copy = styled.div`
  position: relative;
  z-index: 1;
  color: var(--white);
  max-width: 460px;
  padding-right: 16px;

  @media (width < 48rem) {
    max-width: none;
    padding-right: 0;
  }
`;

export const Eyebrow = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--white);
  background: rgb(255 255 255 / 18%);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  padding: 6px 10px;
  border-radius: 999px;
  margin-bottom: 16px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: clamp(28px, 3.4vw, 48px);
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: var(--white);
  text-shadow: 0 2px 12px rgb(0 0 0 / 22%);

  @media (width < 48rem) {
    font-size: 30px;
    line-height: 1.1;
  }
`;

export const TitleEmphasis = styled.em`
  font-style: italic;
  font-weight: 400;
  color: var(--white);
  display: block;
`;

export const Subtitle = styled.p`
  margin: 14px 0 0;
  font-size: 16px;
  line-height: 24px;
  color: rgb(255 255 255 / 90%);
  text-shadow: 0 1px 6px rgb(0 0 0 / 18%);
  max-width: 380px;

  @media (width < 48rem) {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 22px;
  padding: 12px 22px;
  height: 44px;
  background: var(--white);
  color: var(--black);
  border: none;
  border-radius: var(--radius-large);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 8px 18px rgb(0 0 0 / 18%), 0 2px 4px rgb(0 0 0 / 12%);
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:hover {
    background: var(--brand-50);
    box-shadow: 0 10px 22px rgb(0 0 0 / 22%), 0 2px 4px rgb(0 0 0 / 14%);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgb(255 255 255 / 80%), 0 0 0 6px var(--brand-900);
  }

  @media (width < 48rem) {
    height: 42px;
    padding: 10px 18px;
    font-size: 14px;
    margin-top: 16px;
  }
`;

export const SlideImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const SlideVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ArrowWrapper = styled.div`
  & button {
    top: 50%;

    &[data-direction='prev'] {
      left: 4px;
    }
    &[data-direction='next'] {
      right: 4px;
    }
  }
`;

export const DotsPill = styled(CarouselTabs)`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgb(0 0 0 / 28%);
  border-radius: 999px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  @media (width < 48rem) {
    bottom: 14px;
  }
`;

export const Dot = styled(CarouselTab)`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgb(255 255 255 / 55%);
  border: none;
  padding: 0;
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease;

  &[aria-selected='true'] {
    width: 22px;
    background: var(--white);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 2px rgb(0 0 0 / 50%), 0 0 0 4px var(--white);
  }
`;
